import React, { memo, useEffect, useState } from 'react';

import cn from 'classnames';
import $ from 'jquery';
import { useTranslation } from 'react-i18next';

import { getFromLocalStorage, saveToLocalStorage } from '@shared/helpers/localStorageHandler';

import cls from './UiOnboard.module.css';

interface UiOnboardProps {
    classes?: string;
    highlightClasses?: string;
    confirmText?: string;
    title: string;
    subTitle: string;
    localStorageKey: string;
    elementId: string;
    elementContent: React.ReactNode;
    isLeft?: boolean;
    badge?: string;
}

interface Rect {
    top: number;
    left: number;
    width: number;
    height: number;
}

export const UiOnboard = memo(
    ({
        classes,
        highlightClasses,
        confirmText,
        title,
        localStorageKey,
        elementId,
        elementContent,
        isLeft = false,
        badge,
        subTitle,
    }: UiOnboardProps) => {
        const { t } = useTranslation();
        const [isVisible, setIsVisible] = useState(getFromLocalStorage(localStorageKey) === null);
        const [rect, setRect] = useState<Rect | null>(null);

        const hide = () => {
            $('.container-scroller').css({
                height: '',
            });
            setIsVisible(false);
            saveToLocalStorage(localStorageKey, false);
        };

        useEffect(() => {
            if (!isVisible) return undefined;

            if (isVisible) {
                $('.container-scroller').css({
                    height: '100vh',
                });
            }

            const measure = (): boolean => {
                const $el = $(`#${elementId}`);
                if (!$el.length) return false;
                const offset = $el.offset()!;
                setRect({
                    top: offset.top,
                    left: offset.left,
                    width: $el.outerWidth() ?? 0,
                    height: $el.outerHeight() ?? 0,
                });
                return true;
            };

            if (measure()) return undefined;

            const observer = new MutationObserver(() => {
                if (measure()) observer.disconnect();
            });
            observer.observe(document.body, { childList: true, subtree: true });

            $(window).on('resize.uiOnboard', measure);

            return () => {
                observer.disconnect();
                $(window).off('resize.uiOnboard');
            };
        }, [isVisible, elementId]);

        if (!isVisible || !rect) return null;

        const offset = 2;
        const modalLeft = isLeft ? rect.left + rect.width - 455 : rect.left + 50;
        const highlightCenterX = rect.left + rect.width / 2;
        const arrowLeft = Math.round(highlightCenterX - modalLeft);

        return (
            <div className={cn(cls.UiOnboard, classes)}>
                <div
                    className={cn(cls.highlight, highlightClasses)}
                    style={{
                        top: rect.top - offset,
                        left: rect.left - offset,
                        width: rect.width + offset * 2,
                        height: rect.height + offset * 2,
                    }}
                >
                    {elementContent}
                </div>
                <div
                    className={cls.modal}
                    style={{
                        top: rect.top + rect.height + offset + 16,
                        left: modalLeft,
                        ['--arrow-left' as string]: `${arrowLeft}px`,
                    }}
                >
                    <div className={cls.modalContentWrapper}>
                        {badge && <div className={cls.badge}>{badge}</div>}
                        {title && <div className={cls.title}>{title}</div>}
                        {subTitle && <div className={cls.subTitle}>{subTitle}</div>}
                    </div>
                    <div className={cls.modalButton}>
                        <button type="button" onClick={hide}>
                            {confirmText ?? t('button.ok.text')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
);

UiOnboard.displayName = 'UiOnboard';
